import { FC } from "react";
import { useHistory } from "react-router-dom";
import { Box, Chip, Stack, Typography } from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { grey } from "@mui/material/colors";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { isEmpty } from "lodash";
import useBasePath from "src/utils/hooks/useBasePath";
import __ from "src/utils/Translations";
import formatPatternsDay from "src/utils/Company/Absences/formatPatternsDay";
import ActionButton from "src/Pages/Company/Absences/ActionButton/ActionButton";
import {
  BillingCycleConfigurationType,
  PresenceType,
  IAbsenceTypeListItem,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

interface IAbsenceTypesListItemProps {
  item: IAbsenceTypeListItem;
  disabled: boolean;
}

const AbsenceTypesListItem: FC<IAbsenceTypesListItemProps> = ({
  item,
  disabled,
}) => {
  const history = useHistory();
  const basePath = useBasePath();

  return (
    <Box
      sx={{
        "&:not(:last-child)": {
          borderBottom: `1px solid ${grey[300]}`,
          mb: "1.5rem",
        },
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", mb: "0.5rem" }}>
        <BookmarkIcon htmlColor={item.color} sx={{ marginRight: "0.75rem" }} />
        <Typography
          variant="subtitle1"
          data-testid="absence-type-list__item-name"
        >
          {item.name}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
        spacing={2}
      >
        <Box>
          {item.billingCycleConfiguration?.parentAbsenceTypeId && (
            <>
              <Typography
                variant="body1"
                sx={{ fontSize: "0.875rem", m: "-0.25rem 0 0.5rem 2.25rem" }}
                color="text.secondary"
              >
                {__("Wymiar podrzędny dla")}: {item.parentName}
              </Typography>
            </>
          )}
          <Stack
            direction="row"
            sx={{ alignItems: "center", ml: "2rem" }}
            spacing={2}
          >
            <Chip
              label={
                <>
                  {item.presenceType === PresenceType.Absence
                    ? __("Nieobecność")
                    : __("Status obecności")}
                </>
              }
              data-testid="absence-type-presence-type"
            />
            <Chip
              label={
                <>
                  {__("Wymiar")}:{" "}
                  <strong>
                    {
                      // eslint-disable-next-line no-nested-ternary
                      item.billingCycleConfiguration?.type
                        ? item.billingCycleConfiguration.type ===
                          BillingCycleConfigurationType.Yearly
                          ? __("Roczny")
                          : __("Miesięczny")
                        : __("brak")
                    }
                  </strong>
                </>
              }
              data-testid="absence-type-billing-cycle"
            />
            <Chip
              label={
                <>
                  {__("Wzorce naliczania")}:{" "}
                  <strong>
                    {isEmpty(item.billingCycleConfiguration?.patterns)
                      ? __("brak")
                      : formatPatternsDay(
                          item.billingCycleConfiguration?.patterns
                        )}
                  </strong>
                </>
              }
              data-testid="absence-type-billing-patterns"
            />
            <Chip
              label={
                <>
                  {__("Typ nieobecności")}:{" "}
                  <strong>
                    {item.configurationOptions.absencesVisibleForOthers
                      ? __("Widoczny")
                      : __("Ukryty")}
                  </strong>
                </>
              }
              data-testid="absence-type-billing-visibility"
            />
          </Stack>
          <Typography variant="body2" sx={{ m: "1rem 2rem" }}>
            {item.description}
          </Typography>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            marginLeft: "auto",
          }}
        >
          <ActionButton
            label={__("Podgląd szablonu")}
            bgColor="info.main"
            bgColorHover="info.dark"
            color="white"
            margin="0.5rem"
            disabled={disabled}
            onClick={() =>
              history.push(
                `${basePath}/absences/absence-type/${item.id}/preview`
              )
            }
            dataTestId="absence-type-preview-action"
          >
            <ManageSearchIcon />
          </ActionButton>
          <ActionButton
            label={__("Edytuj")}
            bgColor="info.main"
            bgColorHover="info.dark"
            color="white"
            margin="0.5rem"
            disabled={disabled}
            onClick={() =>
              history.push(`${basePath}/absences/absence-type/${item.id}/edit`)
            }
            dataTestId="absence-type-edit-action"
          >
            <EditNoteOutlinedIcon />
          </ActionButton>
        </Box>
      </Stack>
    </Box>
  );
};

export default AbsenceTypesListItem;
